import { useState } from 'react';
import { Button, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalHeader, ModalFooter, MODAL_VARIANTS, MODAL_SIZES } from 'ui/modal';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { getSnippetSlug, getHasSnippets, updateInsertSnippetModalState } from './utils';
import { SnippetSearchList } from '../SnippetSearchList';
import { SnippetDetails } from '../SnippetDetails';
import { NoSnippets } from '../EmptyState';
import { InsertSnippetContainer, FlexModalBody } from '../Styled';
import { useSnippets } from '../../hooks/useSnippets';
import { GraphQLSnippet } from '../../types';

type ModalContent = {
  copy: string;
  header: string;
};

const modalContent: ModalContent = {
  copy: 'Insert snippet',
  header: 'Snippets',
};

type Props = {
  closeModal: () => void;
  onInsertSnippet: ((slugText: string) => void) | undefined;
  'data-portal-id'?: string;
};

const getVariables = () => ({
  sort: { sortBy: 'UpdatedAt', sortDirection: SORT_DIRECTIONS.DESCENDING },
  pagination: { limit: 60, offset: 0 },
});

export const InsertSnippetModal = ({
  closeModal,
  onInsertSnippet,
  'data-portal-id': dataPortalId,
}: Props) => {
  const [selectedSnippet, setSelectedSnippet] = useState({});
  const [snippetSlug, setSnippetSlug] = useState('');
  const { results: snippetList } = useSnippets(
    getVariables,
    updateInsertSnippetModalState((snippets) => {
      setSelectedSnippet(snippets[0]);
      setSnippetSlug(getSnippetSlug(snippets[0]));
    }),
  );

  const hasSnippets = getHasSnippets(snippetList);
  const handleInsertSnippet = () => {
    if (onInsertSnippet) {
      onInsertSnippet(snippetSlug);
      closeModal();
    }
  };

  const handleSelectSnippet = (snippet: GraphQLSnippet) => {
    setSelectedSnippet(snippet);
    const selectedSnippetSlug = getSnippetSlug(snippet);
    setSnippetSlug(selectedSnippetSlug);
  };

  return (
    <Modal
      id="insertSnippetModal"
      withPortal
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
      data-portal-id={dataPortalId}
    >
      <ModalHeader autoFocus={false}>{modalContent.header}</ModalHeader>

      <FlexModalBody>
        {hasSnippets ? (
          <InsertSnippetContainer>
            <SnippetSearchList
              onSelectSnippet={handleSelectSnippet}
              selectedSnippet={selectedSnippet as GraphQLSnippet}
            />
            <SnippetDetails snippet={selectedSnippet as GraphQLSnippet} />
          </InsertSnippetContainer>
        ) : (
          <NoSnippets />
        )}
      </FlexModalBody>

      <ModalFooter>
        {hasSnippets && !!onInsertSnippet && (
          <Button onClick={handleInsertSnippet} size={BUTTON_SIZES.LARGE}>
            {modalContent.copy}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
